import React from "react";
import { Plan, PlanFeatures } from "src/types/plan.types";

const FEATURE_LABELS: Record<keyof PlanFeatures, string> = {
  totalUsers: "team members",
  totalProducts: "products",
  totalSaleOrders: "sale orders / mo",
  totalPurchaseOrders: "purchase orders / mo",
  totalAdjustmentOrders: "stock adjustments / mo",
  totalSuppliers: "suppliers",
};

export const formatPlanPrice = (plan: Plan) => {
  if (plan.planCode === "ENTERPRISE") return "Custom pricing";
  if (!plan.price) return "Free";
  const suffix =
    plan.durationInDays === 30 ? "/ month" : `/ ${plan.durationInDays} days`;
  return `₹${plan.price.toLocaleString("en-IN")} ${suffix}`;
};

// The public pricing-page card — reused by the admin plan-preview modal so
// admins see exactly what customers see, not a re-derived approximation.
const PlanPriceCard: React.FC<{
  plan: Plan;
  ctaLabel?: string;
  onCtaClick?: () => void;
}> = ({ plan, ctaLabel, onCtaClick }) => {
  return (
    <div className="flex flex-col rounded-xl bg-white p-8 shadow dark:bg-gray-900">
      <h2 className="mb-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
        {plan.planName}
      </h2>
      {plan.description && (
        <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
          {plan.description}
        </p>
      )}
      <p className="mb-6 text-xl font-bold text-indigo-600 dark:text-indigo-400">
        {formatPlanPrice(plan)}
      </p>
      {plan.planCode === "ENTERPRISE" ? (
        <p className="mb-8 flex-1 text-left text-sm text-gray-600 dark:text-gray-300">
          Custom setup for your organization — users, products, and order
          volume scaled to fit your team. Talk to us to work out the details.
        </p>
      ) : (
        <ul className="mb-8 flex-1 space-y-2 text-left text-sm text-gray-600 dark:text-gray-300">
          {plan.features &&
            (Object.keys(FEATURE_LABELS) as Array<keyof PlanFeatures>)
              .filter((key) => plan.features?.[key] !== undefined)
              .map((key) => (
                <li key={key}>
                  ✅ {plan.features![key].toLocaleString("en-IN")}{" "}
                  {FEATURE_LABELS[key]}
                </li>
              ))}
        </ul>
      )}

      {ctaLabel && (
        <button
          onClick={onCtaClick}
          className="rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
        >
          {ctaLabel}
        </button>
      )}
    </div>
  );
};

export default PlanPriceCard;
